"use client";

import React from "react";

import TableSortingIcon from "@/components/common/TableSortingIcon";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar } from "@/components/ui/avatar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Ticket,
  TicketStatus,
  TicketPriority,
  TicketType,
} from "@/types/models/ticket";
import { ColumnDef } from "@tanstack/react-table";
import { format } from "date-fns";
import { Clock, MessageCircle } from "lucide-react";

const getStatusColor = (status: TicketStatus) => {
  switch (status) {
    case "open":
      return "bg-blue-500/20 text-blue-600 hover:bg-blue-500/20";
    case "in_progress":
      return "bg-orange-500/20 text-orange-600 hover:bg-orange-500/20";
    case "pending_customer":
      return "bg-yellow-500/20 text-yellow-600 hover:bg-yellow-500/20";
    case "resolved":
      return "bg-green-500/20 text-green-600 hover:bg-green-500/20";
    case "closed":
      return "bg-gray-500/20 text-gray-600 hover:bg-gray-500/20";
    default:
      return "bg-gray-500/20 text-gray-600 hover:bg-gray-500/20";
  }
};

const getPriorityColor = (priority: TicketPriority) => {
  switch (priority) {
    case "low":
      return "bg-green-100 text-green-800 border-green-200";
    case "normal":
      return "bg-yellow-100 text-yellow-800 border-yellow-200";
    case "high":
      return "bg-orange-100 text-orange-800 border-orange-200";
    case "urgent":
      return "bg-red-100 text-red-800 border-red-200";
    default:
      return "bg-gray-100 text-gray-800 border-gray-200";
  }
};

const formatStatus = (status: TicketStatus) => {
  const normalizedStatus = String(status);

  switch (status) {
    case "open":
      return "Open";
    case "in_progress":
      return "In Progress";
    case "pending_customer":
      return "Pending Customer";
    case "resolved":
      return "Resolved";
    case "closed":
      return "Closed";
    default:
      return normalizedStatus.replace(/_/g, " ");
  }
};

const formatType = (type: TicketType) => {
  const normalized = String(type).toLowerCase();
  switch (normalized) {
    case "technical":
      return "Technical";
    case "general":
      return "General";
    case "website":
      return "Website";
    case "products":
      return "Products";
    case "orders":
      return "Orders";
    case "payments":
      return "Payments";
    case "shipping":
      return "Shipping";
    case "billing":
      return "Billing";
    case "account":
      return "Account";
    case "other":
      return "Other";
    default:
      return type;
  }
};

const formatParticipantRole = (role?: string) => {
  if (!role) return "Unknown";
  return role
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const ParticipantsPopover = ({
  participants,
  trigger,
}: {
  participants: Ticket["participants"];
  trigger: React.ReactNode;
}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  if (!participants || participants.length === 0) {
    return <>{trigger}</>;
  }

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div
          className="cursor-pointer"
          onMouseEnter={() => setIsOpen(true)}
          onMouseLeave={() => setIsOpen(false)}
        >
          {trigger}
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-0 bg-card border border-border/40 shadow-lg"
        align="start"
        side="right"
        sideOffset={-16}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
      >
        <div className="p-4">
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {participants.map((participant) => (
              <div
                key={participant.id}
                className="flex items-center gap-3 rounded-md border border-border/30 p-3 hover:bg-muted/30 transition-colors"
              >
                <Avatar
                  name={
                    participant.participantName ||
                    participant.participantEmail ||
                    "Participant"
                  }
                  className="h-8 w-8"
                />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">
                    {participant.participantName ||
                      participant.participantEmail ||
                      "Participant"}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {participant.participantEmail || "No email"}
                  </div>
                </div>
                <Badge
                  variant="outline"
                  className="text-[10px] px-2 py-0.5 shrink-0"
                >
                  {formatParticipantRole(participant.role)}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const columns: ColumnDef<Ticket>[] = [
  {
    id: "select",
    size: 50,
    minSize: 40,
    maxSize: 60,
    header: ({ table }) => {
      const { rows } = table.getRowModel();
      const selectedRows = table.getSelectedRowModel().rows;
      const isAllSelected =
        rows.length > 0 && selectedRows.length === rows.length;
      const isSomeSelected = selectedRows.length > 0 && !isAllSelected;

      return (
        <div className="flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
          <Checkbox
            checked={
              isAllSelected ? true : isSomeSelected ? "indeterminate" : false
            }
            onCheckedChange={(value) => {
              if (value) {
                table.toggleAllPageRowsSelected(true);
              } else {
                table.toggleAllPageRowsSelected(false);
              }
            }}
            aria-label="Select all"
            className="!bg-transparent border-foreground text-foreground data-[state=checked]:text-foreground"
          />
        </div>
      );
    },
    cell: ({ row }) => (
      <div className="flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="!bg-transparent border-foreground text-foreground data-[state=checked]:text-foreground"
        />
      </div>
    ),
  },
  {
    accessorKey: "id",
    size: 120,
    minSize: 100,
    maxSize: 150,
    header: ({ column }) => {
      return (
        <Button
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          variant="ghost"
          className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit group"
        >
          <span className="text-base font-bold">Ticket ID</span>
          <div
            className={`transition-opacity duration-200 ${column.getIsSorted() ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
          >
            <TableSortingIcon sortDirection={column.getIsSorted()} />
          </div>
        </Button>
      );
    },
    cell: ({ row }) => {
      const ticketId = row.getValue("id") as string;
      const date = new Date(row.original.created_at);
      const formattedDate = format(date, "MMM dd yyyy");
      return (
        <div>
          <h3 className="font-mono text-xs font-semibold">
            #{ticketId?.slice(-8) || "N/A"}
          </h3>
          <span className="text-xs text-muted-foreground">{formattedDate}</span>
        </div>
      );
    },
  },
  {
    accessorKey: "subject",
    size: 300,
    minSize: 200,
    maxSize: 400,
    header: ({ column }) => {
      return (
        <Button
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          variant="ghost"
          className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit group"
        >
          <span className="text-base font-bold">Subject</span>
          <div
            className={`transition-opacity duration-200 ${column.getIsSorted() ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
          >
            <TableSortingIcon sortDirection={column.getIsSorted()} />
          </div>
        </Button>
      );
    },
    cell: ({ row }) => {
      const title = row.getValue("subject") as string;
      const type = row.original.type;
      const unreadCount = row.original.unreadCount || 0;

      return (
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <h3
              className="text-sm font-semibold truncate max-w-[250px]"
              title={title}
            >
              {title}
            </h3>
            {unreadCount > 0 && (
              <span className="bg-primary text-primary-foreground text-xs px-2 py-0.5 rounded-full">
                {unreadCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {formatType(type || "general")}
            </Badge>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "reporterName",
    size: 200,
    minSize: 150,
    maxSize: 250,
    header: ({ column }) => {
      return (
        <Button
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          variant="ghost"
          className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit group"
        >
          <span className="text-base font-bold">Reporter</span>
          <div
            className={`transition-opacity duration-200 ${column.getIsSorted() ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
          >
            <TableSortingIcon sortDirection={column.getIsSorted()} />
          </div>
        </Button>
      );
    },
    cell: ({ row }) => {
      const reporterParticipant = row.original.participants?.find(
        (participant) => participant.role === "requester",
      );
      const reporterName =
        reporterParticipant?.participantName ||
        reporterParticipant?.participantEmail ||
        "Reporter";
      const reporterEmail = reporterParticipant?.participantEmail;

      return (
        <div className="flex items-center gap-3">
          <Avatar name={reporterName} className="h-8 w-8" />
          <div>
            <h3
              className="text-xs font-semibold truncate max-w-[120px]"
              title={reporterName}
            >
              {reporterName}
            </h3>
            <span
              className="text-xs text-muted-foreground truncate block max-w-[120px]"
              title={reporterEmail}
            >
              {reporterEmail || "No email"}
            </span>
          </div>
        </div>
      );
    },
  },
  {
    id: "participants",
    size: 220,
    minSize: 120,
    maxSize: 260,
    header: () => <span className="text-base font-bold">Participants</span>,
    cell: ({ row }) => {
      const participants = row.original.participants || [];

      return (
        <ParticipantsPopover
          participants={participants}
          trigger={
            <div className="flex flex-wrap gap-2">
              {participants.length > 0 ? (
                participants.slice(0, 2).map((participant) => (
                  <div
                    key={participant.id}
                    className="flex items-center gap-2 rounded-full border px-2 py-1 bg-muted/30 max-w-full"
                  >
                    <Avatar
                      name={
                        participant.participantName ||
                        participant.participantEmail ||
                        "Participant"
                      }
                      className="h-5 w-5"
                    />
                    <span
                      className="text-xs font-medium truncate max-w-[120px]"
                      title={
                        participant.participantName ||
                        participant.participantEmail ||
                        "Participant"
                      }
                    >
                      {participant.participantName ||
                        participant.participantEmail ||
                        "Participant"}
                    </span>
                    <Badge
                      variant="outline"
                      className="text-[10px] px-1.5 py-0"
                    >
                      {formatParticipantRole(participant.role)}
                    </Badge>
                  </div>
                ))
              ) : (
                <span className="text-xs text-muted-foreground">
                  No participants
                </span>
              )}
              {participants.length > 2 && (
                <span className="text-xs text-muted-foreground self-center">
                  +{participants.length - 2} more
                </span>
              )}
            </div>
          }
        />
      );
    },
  },
  {
    accessorKey: "priority",
    size: 100,
    minSize: 80,
    maxSize: 120,
    header: ({ column }) => {
      return (
        <Button
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          variant="ghost"
          className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit group"
        >
          <span className="text-base font-bold">Priority</span>
          <div
            className={`transition-opacity duration-200 ${column.getIsSorted() ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
          >
            <TableSortingIcon sortDirection={column.getIsSorted()} />
          </div>
        </Button>
      );
    },
    cell: ({ row }) => {
      const priority = row.getValue("priority") as TicketPriority;
      return (
        <Badge
          variant="outline"
          className={`text-xs border ${getPriorityColor(priority)}`}
        >
          {priority}
        </Badge>
      );
    },
  },
  {
    accessorKey: "lastMessageAt",
    size: 160,
    minSize: 120,
    maxSize: 200,
    header: ({ column }) => {
      return (
        <Button
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
          variant="ghost"
          className="w-full flex p-0 items-center justify-between hover:!bg-transparent hover:text-inherit group"
        >
          <span className="text-base font-bold">Last Activity</span>
          <div
            className={`transition-opacity duration-200 ${column.getIsSorted() ? "opacity-100" : "opacity-0 group-hover:opacity-100"}`}
          >
            <TableSortingIcon sortDirection={column.getIsSorted()} />
          </div>
        </Button>
      );
    },
    cell: ({ row }) => {
      const lastMessageAt = row.getValue("lastMessageAt") as string;
      const messageCount = row.original.messageCount;
      const lastActivity = lastMessageAt
        ? new Date(lastMessageAt)
        : new Date(row.original.created_at);
      const formattedDate = format(lastActivity, "MMM dd, HH:mm");

      return (
        <div className="space-y-1">
          <div className="flex items-center gap-1">
            <Clock className="h-3 w-3 text-muted-foreground" />
            <span className="text-xs font-medium">{formattedDate}</span>
          </div>
          <div className="flex items-center gap-1">
            <MessageCircle className="h-3 w-3 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">
              {messageCount} message{messageCount !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
      );
    },
  },
  {
    accessorKey: "status",
    size: 120,
    minSize: 100,
    maxSize: 150,
    header: () => <span className="text-base font-bold">Status</span>,
    cell: ({ row }) => {
      const status = row.getValue("status") as TicketStatus;
      return (
        <Badge
          variant="secondary"
          className={`rounded-full px-[10px] py-[5px] ${getStatusColor(status)}`}
        >
          {formatStatus(status)}
        </Badge>
      );
    },
  },
];
